<template>
  <tr class="nested-fields">
    <td>
      <search-products
        :supplierId="supplierId"
        :productRequestItem="productRequestItem"
        :handleProductSelection="handleProductSelection"
      />
      <tooltip :tooltip-text="errors['product_supplier']" :hidden="!errors['product_supplier']">
        <i class="ri-error-warning-fill text-danger" v-if="errors['product_supplier']"></i>
      </tooltip>
    </td>
    <td>
      <input
        class="form-control"
        type="number"
        readonly="readonly"
        :aria-label="t('activerecord.attributes.product_request_item.price_per_item')"
        v-model="productRequestItem.price_per_item"
      />
    </td>
    <td>
      <small class="text-muted">
        {{ shopifyVariantDescription }}
      </small>
    </td>
    <td>
      <input
        :class="{ 'form-control': true }"
        min="1"
        :style="{ 'background-color': quantityValidationError ? '#FFF3E0' : 'white' }"
        type="number"
        :aria-label="t('activerecord.attributes.product_request_item.requested_quantity')"
        :aria-invalid="!!errors['requested_quantity']"
        v-model.number="productRequestItem.requested_quantity"
      />
      <tooltip :tooltip-text="errors['requested_quantity']" :hidden="!errors['requested_quantity']">
        <i class="ri-error-warning-fill text-danger" aria-hidden="true" v-if="errors['requested_quantity']"></i>
      </tooltip>
    </td>
    <td>
      <flat-pickr
        class="form-control"
        name="requestedDeliveryAt"
        :placeholder="t('activerecord.attributes.product_request_item.requested_delivery_at')"
        :aria-label="t('activerecord.attributes.product_request_item.requested_delivery_at')"
        :aria-invalid="!!errors['requested_delivery_at']"
        v-model="productRequestItem.requested_delivery_at"
        :config="config"
      />
      <tooltip :tooltip-text="errors['requested_delivery_at']" :hidden="!errors['requested_delivery_at']">
        <i class="ri-error-warning-fill text-danger" aria-hidden="true" v-if="errors['requested_delivery_at']"></i>
      </tooltip>
    </td>
    <td>
      <input
        class="form-control"
        type="number"
        min="1.0"
        :aria-label="t('activerecord.attributes.product_request_item.tax_rate')"
        :aria-invalid="!!errors['tax_rate']"
        v-model="productRequestItem.tax_rate"
      />
      <tooltip :tooltip-text="errors['tax_rate']" :hidden="!errors['tax_rate']">
        <i class="ri-error-warning-fill text-danger" aria-hidden="true" v-if="errors['tax_rate']"></i>
      </tooltip>
    </td>
    <td>
      <textarea
        class="form-control"
        :aria-label="t('activerecord.attributes.product_request_item.comment')"
        v-model="productRequestItem.comment"
      />
    </td>
    <td>
      <textarea
        class="form-control"
        :aria-label="t('activerecord.attributes.product_request_item.internal_comment')"
        v-model="productRequestItem.internal_comment"
      />
    </td>
    <td>
      <Tooltip
        :tooltip-text="t('features.product_request.product_request_items.delete_item_tip')"
        :hidden="productRequestStore.canRemoveProductRequestItem(productRequestItem.id, index)"
      >
        <button
          class="btn btn-light text-danger"
          type="button"
          :aria-label="t('features.product_request.product_request_items.delete_item')"
          @click.prevent="confirmDeleteProductRequestItem"
          :disabled="!productRequestStore.canRemoveProductRequestItem(productRequestItem.id, index)"
        >
          <i class="ri-close-line" aria-hidden="true" />
        </button>
      </Tooltip>
    </td>
  </tr>
  <tr>
    <td colspan="9">
      <div class="row">
        <div class="col-sm-8 form-group">
          <label>{{ t('activerecord.models.delivery_plan_batch.one') }}</label>
          <request-item-batch
            v-for="(plannedBatchItem, index) in productRequestItem.planned_batch_items"
            :index="index"
            :key="plannedBatchItem.id"
            :totalQuantity="productRequestItem.requested_quantity"
            :batches="allDeliveryPlanBatches"
            :plannedBatchItem="plannedBatchItem"
            :deletePlannedBatchItem="deletePlannedBatchItem"
            :errors="errors"
          />
        </div>
        <div class="col-sm-4">
          <div
            v-if="quantityValidationError"
            class="alert alert-warning d-flex align-items-center mt-4"
            role="alert"
          >
            <div class="ml-2">
              {{
                t(
                  'features.product_request.planned_batch_items.total_quantity_is_different_warning',
                  {
                    total_quantity: productRequestItem.requested_quantity,
                    quantity_in_batches: quantityInBatches
                  }
                )
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="row" v-if="defaultBatchToAttach">
        <div class="col-sm-12">
          <button class="btn btn-secondary ml-2" @click="addNewPlannedBatchItem">
            {{ t('features.product_request.planned_batch_items.split_to_another_batch') }}
          </button>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import FlatPickr from 'vue-flatpickr-component'
import SearchProducts from './search-products.vue'
import RequestItemBatch from './delivery-plans/request-item-batch.vue'
import Tooltip from '../tooltip'
import {mapStores} from "pinia";
import {useProductRequestStore} from "../../stores/productRequestStore";

export default {
  inject: ['i18n'],
  components: {
    Tooltip,
    FlatPickr,
    SearchProducts,
    RequestItemBatch
  },
  props: {
    index: {
      type: Number,
      required: true
    },
    productRequestId: {
      required: true,
      type: Number
    },
    supplierId: {
      required: true,
      type: Number
    },
    productRequestItem: {
      required: true,
      type: Object
    },
    deleteProductRequestItem: {
      required: true,
      type: Function
    },
    allDeliveryPlanBatches: {
      required: true,
      type: Array
    },
    removedPlannedBatchItems: {
      required: true,
      type: Array
    },
    errors: {
      required: false,
      type: Object
    }
  },
  computed: {
    config() {
      return {
        dateFormat: 'Y/m/d H:i',
        altFormat: 'Y/m/d H:i',
        altInput: true,
        enableTime: true,
        enableSeconds: false,
        time_24hr: true,
        defaultHour: 0,
        defaultMinute: 0
      }
    },
    quantityInBatches() {
      return this.productRequestStore.quantityInBatches(this.productRequestItem.id, this.index)
    },
    defaultBatchToAttach() {
      return this.productRequestStore.defaultBatchToAttach()
    },
    quantityValidationError() {
      return this.productRequestItem.requested_quantity !== this.quantityInBatches
    },
    shopifyVariantDescription() {
      const item = this.productRequestItem
      if (!item) return ''
      const dest = item.stock_destination
      const label = this.t('features.product_request.zaku.shopify_variant')
      if (dest && dest.name) {
        return `${label}: ${dest.name}${dest.sku ? ` (${dest.sku})` : ''}`
      }
      return item.product_variant_id ? `${label} #${item.product_variant_id}` : '—'
    },
    ...mapStores(useProductRequestStore)
  },

  data() {
    return {
      t: (key, options) => this.i18n.t(key, options)
    }
  },
  methods: {
    handleProductSelection(selectedProduct) {
      this.productRequestStore.applySelectedProduct(this.productRequestItem.id, this.index, selectedProduct)
    },
    confirmDeleteProductRequestItem() {
      const message = this.t('features.product_request.product_request_items.delete_item_confirm')
      if (window.confirm(message)) {
        this.deleteProductRequestItem(this.index, this.productRequestItem)
      }
    },
    addNewPlannedBatchItem() {
      this.productRequestStore.addNewPlannedBatchItem(this.productRequestItem.id, this.index)
    },
    deletePlannedBatchItem(plannedBatchIndex, batchItem) {
      this.productRequestStore.deletePlannedBatchItem(this.productRequestItem.id, this.index, plannedBatchIndex, batchItem)
    }
  }
}
</script>
